import { IntilioCodes } from '@shared/enums/initilio-codes.enum';
import { HttpError } from '@shared/interfaces/error.interface';
import { SnackBarService } from '@core/services/snackbar.service';
import { TranslateService } from '@ngx-translate/core';
import { TaskService } from '@shared/modules/tasks/services/task-service';
import { UntypedFormControl, Validators } from '@angular/forms';
import { Config } from '@shared/configs/config';
import { ButtonSize, ButtonTypes } from '@shared/modules/ui/components/button/button.component';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject } from '@angular/core';
import { ListTaskEventType, ListTaskService } from '@shared/modules/list/services/list-task.service';
import { TaskGroup } from '@shared/models/task-group.model';
import { RemoveModalController } from '@shared/controllers/remove-modal.controller';
import { ConfirmationDiscardModalComponent } from '@shared/components/confirmation-discard-modal/confirmation-discard-modal.component';
import { TaskType } from '@shared/enums/task-type.enum';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';

@Component({
  templateUrl: './task-group-modal.component.html',
  styles: [
    `
      .new-modal {
        min-width: initial;
      }
    `
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TaskGroupModalComponent {
  Config = Config;
  ButtonTypes = ButtonTypes;
  ButtonSize = ButtonSize;

  loading: boolean = false;

  group: UntypedFormControl = new UntypedFormControl(null, [Validators.required]);

  onGroupAdd: EventEmitter<string> = new EventEmitter<string>();
  onGroupRemove: EventEmitter<TaskGroup> = new EventEmitter<TaskGroup>();

  get errorMessages() {
    return Config.validationMessages;
  }

  constructor(
    private dialogRef: MatDialogRef<TaskGroupModalComponent>,
    private changes: ChangeDetectorRef,
    private taskService: TaskService,
    private t: TranslateService,
    private s: SnackBarService,
    private listTaskService: ListTaskService,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      projectId: number;
      notSendNewGroup: boolean;
      edit?: boolean;
      taskGroup?: TaskGroup;
      taskType?: TaskType;
      groupType?: 'project' | 'without_project';
    }
  ) {
    dialogRef.disableClose = true;

    dialogRef.backdropClick().subscribe(() => this.cancel());
  }

  cancel(): void {
    if (this.group.pristine) {
      this.group.reset();
      this.dialogRef.close();
    } else {
      let dialogRef = this.dialog.open(ConfirmationDiscardModalComponent, {
        width: Config.DEFAULT_MODAL_WIDTH,
        autoFocus: false
      });

      dialogRef.afterClosed().subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.group.reset();
          this.dialogRef.close();
        }
      });
    }
  }

  submit(): void {
    this.addNewTaskGroup();
  }

  addNewTaskGroup(): void {
    this.group.markAsTouched();

    if (!this.group.valid || this.loading) return;

    if (this.data.notSendNewGroup) {
      this.onGroupAdd.emit(this.group.value);
      this.group.reset();
      this.dialogRef.close();
      return;
    }

    this.loading = true;
    this.changes.detectChanges();

    if (this.data.projectId) {
      this.taskService
        .createTaskGroup(this.group.value, this.data.projectId, this.data.taskType)
        .subscribe({
          next: this.successCreateTaskGroup.bind(this),
          error: this.errorCreateTaskGroup.bind(this)
        })
        .add(() => {
          this.loading = false;
          this.changes.detectChanges();
        });
    } else {
      this.taskService
        .createWorkspaceTaskGroup(this.group.value, this.data.groupType)
        .subscribe({
          next: this.successCreateTaskGroup.bind(this),
          error: this.errorCreateTaskGroup.bind(this)
        })
        .add(() => {
          this.loading = false;
          this.changes.detectChanges();
        });
    }
  }

  successCreateTaskGroup(taskGroup: TaskGroup): void {
    this.onGroupAdd.emit(this.group.value);
    this.s.success(this.t.instant('Projects.taskGroupCreated'));
    this.group.reset();
    this.dialogRef.close();
    this.listTaskService.emitter.emit({
      type: ListTaskEventType.ADD_TASK_GROUP,
      data: { taskGroup, groupType: this.data.groupType }
    });
  }

  errorCreateTaskGroup(error: HttpError): void {
    switch (error?.messageCode) {
      case IntilioCodes.ALREADY_EXISTS:
        this.s.error(this.t.instant('Projects.taskGroupFailedExists'));
        break;
      default:
        this.s.error(this.t.instant('Projects.taskGroupFailed'));
        break;
    }
  }

  onRemoveGroup(): void {
    const ctrl = new RemoveModalController();
    ctrl
      .remove(() => {
        this.group.reset();
        this.dialogRef.close();
        this.onGroupRemove.emit(this.data.taskGroup);
      })
      .subscribe();
  }
}
